/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Emitter from '@exc/emitter';
import {encodeForRedirect} from '@exc/url';
import {getOrgs, getToken, isSessionEnvValid} from './utils/IMSUtils';
import {GraphQlService} from '@exc/graphql';
import hashString from 'string-hash';
import {Internal} from '@adobe/exc-app/internal';
import logout from './utils/logout';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {storage} from '@exc/storage';
import Visible from './utils/visibility';

/**
 * IMS Validate Token scheduler default interval in millis
 */
const QA_INTERVAL = 120000;
const PROD_INTERVAL = 30000;
const BACKGROUND_INTERVAL = 1000 * 60 * 10;

let imsLoaded = false;

class Auth extends Emitter {
  load(configuration) {
    const {env, imsConfig, listeners} = configuration;
    this.env = env;
    this.imsEnv = imsConfig.environment || this.env;
    this.backupAccessTokensInfos = {};

    this.addListenersToAuth(listeners);
    this.logout = logout;
    this.visibility = new Visible();
    this.metrics = metrics.create('exc.auth.Auth');
    this.metrics.event('Auth.init');

    const onError = err => {
      // `err` is not always a string, so let's convert to an object for
      // consistent handling later on.
      if (typeof err === 'string') {
        err = {error: err};
      }
      this.metrics.error(`Error making a request in IMS ${imsLoaded ? 'after' : 'during'} init`, err);
      const {debug, error, name} = err;
      const status = ((debug || {}).url || {}).error;
      // The request was aborted and we don't really know why.
      if ((error === 'aborted' || error === 'initialize_error') && !imsLoaded) {
        this.emit('auth.initializeError');
        return;
      }
      // There are a variety of cases that can trigger rate limit errors.
      if (error === 'rate_limited' || status === 'rate_limited' || name === 'rate_limit') {
        this.emit('auth.rateLimited');
      }
    };
    window['exc-ims-error-handler'] = onError;

    if (!imsLoaded) {
      return window['exc-ims'].then(({adobeIMS, adobeid}) => {
        adobeid.storedEvents.forEach(event => this.emit(event));
        adobeid.onError = onError;
        this.adobeIMS = adobeIMS;
        this.adobeid = adobeid;

        this.visibility.on('hidden', this.handleHidden);
        this.visibility.on('shown', this.handleShown);

        if (!imsLoaded) {
          imsLoaded = true;
          this.isSessionEnvValid(imsConfig.environment, this.imsEnv);
          this.setupValidate(this.imsEnv === 'prod' ? PROD_INTERVAL : QA_INTERVAL);
        }
        delete window['exc-ims'];
        delete window['exc-ims-error-handler'];
        return this;
      });
    }
    return this;
  }

  isAuthenticated(force) {
    return this.adobeIMS.isSignedInUser() || force;
  }

  signIn() {
    this.clear();
    // If the hash is not already encoded (e.g. user is logged out and clicks on a bookmarked link)
    // we should encode the hash to properly redirect once the user is logged in.
    const redirect_uri = window.location.href.includes('old_hash') ?
      window.location.href : encodeForRedirect();
    this.adobeIMS.signIn({locale: navigator.language, redirect_uri});
  }

  /**
   * A scheduler to poll on ims validate token.
   * @param {number} interval Polling interval in millis.
   */
  setupValidate(interval) {
    this.setIntervalId = setInterval(this.validateTokenAndEmit, interval);
    return this.setIntervalId;
  }

  /**
   * A helper to emit and auto clear token status and login status
   * Generates: auth.validatetokensuccess event - on successful validation.
   * Generates: auth.validatetokenfailed event - on invalid/expired token and clears login.
   * @returns {boolean} returns if the session is valid or not
   */
  validateTokenAndEmit = async () => {
    try {
      await this.adobeIMS.validateToken();
      this.emit('auth.validatetokensuccess');
      return true;
    } catch (err) {
      this.emit('auth.validatetokenfailed');
      return false;
    }
  }

  /** Gets session Id from access token. */
  getSessionId() {
    const accessTokenInfo = this.fetchAccessTokenInfo();
    return accessTokenInfo && accessTokenInfo.sid;
  }

  /**
    * TO DO: Get this value via a more stable IMSLib API
    * Gets userId from access token.
    */
  getHashedUserId() {
    const accessTokenInfo = this.adobeIMS.tokenService.getTokenFieldsFromStorage();
    const userId = (accessTokenInfo || {}).user_id;
    return userId && hashString(userId).toString();
  }

  /**
    * Gets hashed authId from profile.
    */
  getHashedAuthId() {
    return hashString(this.authId).toString();
  }

  /**
   * On page shown this will:
   *   - if it's been in the background for > 30 seconds validate immediately
   *   - setup the validation loop
   */
  handleShown = async () => {
    if (!this.isAuthenticated()) {
      return;
    }
    clearInterval(this.setIntervalId);
    const timeSinceBackground = Date.now() - this.backgroundRefreshStart;
    if (timeSinceBackground > 30000) {
      const isValid = await this.validateTokenAndEmit();
      if (!isValid) {
        return;
      }
    }
    this.setupValidate(this.imsEnv === 'prod' ? PROD_INTERVAL : QA_INTERVAL);
  }

  /**
   * A helper to clear default validation loop, store time placed in background, and
   * add a new 10 minute wait loop to reduce IMS load.
   */
  handleHidden = () => {
    if (!this.isAuthenticated()) {
      return;
    }
    clearInterval(this.setIntervalId);
    this.backgroundRefreshStart = Date.now();
    this.setupValidate(BACKGROUND_INTERVAL);
  }

  /**
   * Function used to set a custom token. This function is only used for e2e tests on dev and QA.
   * @param {object} token - Token object to be set
   */
  async setCustomToken(token) {
    this.adobeid.standalone = {};
    // expirems needs to be positive for setStandAloneToken to work, but the value
    // is not actually read.
    await this.adobeIMS.setStandAloneToken({expirems: 600000, token});
  }

  /**
   * Get stored profile from IMS lib.
   * @param {boolean} signOutIfNone - Sign out if no profile is available (Default: true)
   * @returns {IMSProfile} profile
   */
  async fetchUserProfile(signOutIfNone = true) {
    const profile = await this.adobeIMS.getProfile();
    if (profile) {
      this.authId = profile.authId || profile.userId;
      return profile;
    }
    // There is a session, so likely the profile is failing to load for some
    // reason related to 429 or other error from the IMS request. There will
    // likely be an error shown to the user, so we don't need to log out.
    if (this.adobeIMS.getAccessToken() || !signOutIfNone) {
      return null;
    }
    this.signOut(true);
  }

  // add caching here separately if required
  // Fetch token for custom client ID and scopes
  async fetchCustomImsAccessToken(clientId, scope) {
    if (!clientId || !scope) {
      return null;
    }

    return await getToken(clientId, scope, this.imsEnv);
  }

  fetchAccessToken() {
    const accessTokenInfo = this.fetchAccessTokenInfo();
    return accessTokenInfo && accessTokenInfo.token;
  }

  fetchAccessTokenInfo() {
    return this.adobeIMS.getAccessToken();
  }

  async signOut() {
    if (this.isAuthenticated()) {
      await this.logout.invoke();
    }
    this.clear();
    this.adobeIMS.signOut({redirect_uri: encodeForRedirect()});
  }

  clear() {
    Internal.clearUser();
    storage.local.clearOnLogout();
    storage.session.clear();
    this.logout.remove();
    this.setIntervalId && clearInterval(this.setIntervalId);
  }

  /**
   * Get org id to org name map.
   * @returns {Object} Org name map.
   */
  fetchOrgs() {
    this.metrics.event('failed to fetch org data from gql', {level: Level.ERROR});
    return getOrgs(this.getClientId(), this.imsEnv);
  }

  /**
   * Checks if all org names for profile are available.
   * @param {array} orgsMap - Array of org names
   * @param {IMSProfile} profile - IMS Profile
   *
   * @returns {boolean} True if all profile orgs are in map, false otherwise.
   */
  allOrgsInProfile(orgsMap, profile) {
    try {
      if (orgsMap && orgsMap.length && profile) {
        const pcEntries = profile.projectedProductContext.map(pc => pc.prodCtx.owningEntity);
        const orgs = orgsMap.map(org => `${org.orgRef.ident}@${org.orgRef.authSrc}`);
        if (pcEntries.every(org => orgs.includes(org))) {
          return true;
        }
        this.metrics.event('Auth.cachedOrgsStale');
        return false;
      }
      profile && this.metrics.event('Auth.noCachedOrgs');
    } catch (err) {
      this.metrics.event('Auth.cachedOrgsError', {err: err.toString()});
    }
    return false;
  }

  addListenersToAuth(listeners) {
    listeners &&
      Object.keys(listeners).forEach(event => {
        this.on(event, listeners[event]);
      });
  }

  getClientId() {
    return this.adobeid.client_id;
  }

  /**
   * Returns isFromIMS value from adobeid object.
   * @returns {boolean} Will be true when redirected back from the IMS server.
   */
  fromIms() {
    return this.adobeid.isFromIMS;
  }

  async switchProfile(userId) {
    await this.adobeIMS.switchProfile(userId, {
      client_id: this.getClientId(),
      prompt: 'none',
      response_type: 'token',
      scope: this.adobeid.scope,
      target_profile: userId
    });
  }

  isSessionEnvValid(imsEnv, buildEnv) {
    if (!isSessionEnvValid(imsEnv, buildEnv)) {
      this.metrics.event('Auth.ims.conflict', {level: Level.ERROR});
      this.signOut(true);
    }
  }

  /**
   * Determines whether or not to sign out the user after an error.
   * @param error
   * @param description
   * @returns {boolean} True if user should be signed out, false otherwise.
   */
  async signoutOnError(error, description) {
    const errors = (error || {}).errors || {};
    if (401 in errors) {
      const isValid = await this.validateTokenAndEmit();
      this.metrics.info('401 error', {validToken: isValid});
      if (!isValid) {
        await this.signOut();
        return true;
      }
      return false;
    }
    if (429 in errors) {
      this.emit('auth.rateLimited');
      return true;
    }
    this.metrics.error(`${description} Load error: ${Object.keys(errors).join(', ')}`, error);
    return false;
  }

  /**
   * Gets additional user data from GraphQL (Account Cluster & Behance)
   * @param imageKeySize
   * @returns {Promise<*>} Additional User Data
   */
  async getAdditionalUserData(imageKeySize) {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.getAdditionalUserData({imageKeySize});
    } catch (error) {
      await this.signoutOnError(error, 'Account Cluster');
    }
  }

  async gqlOrgLaunchDarkly() {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.launchDarklyFeatureFlags();
    } catch (error) {
      this.metrics.error('GQL failed to fetch Launch Darkly feature flags');
      throw error;
    }
  }

  /**
   * Gets user permissions for the current IMS org from GraphQL.
   * @returns {Promise<Permission[]>} Permission array
   */
  async gqlUserPermissions() {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.getUserPermissions();
    } catch (error) {
      this.metrics.error('GQL failed to fetch user permissions from PALM');
    }
  }

  /**
   * Gets user sandboxes for the current IMS org from GraphQL.
   * @param {string} selectedImsOrg Current IMS Org
   * @returns {Promise<Sandbox[]>} Sandboxes array
   */
  async gqlUserSandboxes(selectedImsOrg) {
    let variables;
    // Based on where AEP (serviceCode acp) was provisioned, we need to get the user sandboxes from
    // platform-stage when Shell is running on QA/Dev which are normally linked to platform-int.
    // The fulfillable data field provides the correct environment to the gql service.
    if (this.env === 'dev' || this.env === 'qa') {
      const {projectedProductContext} = await this.fetchUserProfile();
      const {prodCtx} = projectedProductContext.find(({prodCtx}) =>
        prodCtx.serviceCode === 'acp' &&
        prodCtx.owningEntity === selectedImsOrg &&
        prodCtx.fulfillable_data
      ) || {};
      variables = prodCtx && {fulfillableData: JSON.parse(prodCtx.fulfillable_data)};
    }
    const gqlService = new GraphQlService();
    try {
      // TO DO: Remove fulfillableData
      return await gqlService.getUserSandboxes(variables);
    } catch (error) {
      await this.signoutOnError(error, 'User Sandboxes');
    }
  }

  /**
   * Get a new access token from IMS
   */
  refreshToken() {
    return this.adobeIMS.refreshToken();
  }

  /**
   * Gets Header data from GraphQL (Pulse)
   * @param {string} locale User Locale
   * @returns {Promise<*>} Header data
   */
  async gqlHandlerForHeader(locale, getProfile) {
    const gqlService = new GraphQlService();
    try {
      return await gqlService.initializeShellDataForHeader({
        config_version: 0,
        locale,
        settings: {
          lastClickTimestamp: '',
          lastNotificationSeenTimestamp: '',
          lastSeenAnnouncementsTimestamp: ''
        }
      }, getProfile);
    } catch (error) {
      await this.signoutOnError(error, 'GQL Header');
    }
  }

  /**
   * Get initial data for Shell - User Profile, Org & Login data from GraphQL
   * @param {boolean} isReload - True for reload, false for Login
   * @returns {Promise<*>} Initial data
   */
  async gqlHandler(isReload) {
    // Grab the org from the path and use that as the selected org. If there
    // is no org yet, use the selected one from IMS.
    const gqlService = new GraphQlService();
    let shellInitData;
    try {
      if (isReload) {
        shellInitData = await gqlService.initializeShellDataForReload();
      } else {
        // Login
        shellInitData = await gqlService.initializeShellDataForLogin();
        await this.updateProfile((shellInitData || {}).userProfileJson);
      }

      return shellInitData;
    } catch (error) {
      if (!await this.signoutOnError(error, 'GQL') && !isReload) {
        await this.updateProfile((shellInitData || {}).userProfileJson).catch(() => {
          this.metrics.error('GQL and IMS failed to fetch user profile');
          this.emit('auth.apiFailure');
        });
      }
    }
  }

  async updateProfile(profile) {
    if (!profile) {
      return await this.adobeIMS.getProfile();
    } else {
      // set fetched profile from gql in IMS Session storage
      this.adobeIMS.profileService.saveProfileToStorage(profile);
      this.authId = profile.authId || profile.userId;
      return profile;
    }
  }
}

let instance = new Auth();
let loading = null;

export const AuthInstance = async config => {
  if (!instance.ready) {
    if (!loading) {
      loading = instance.load(config);
    }

    return loading
      .catch(err => {
        throw err;
      })
      .then(() => {
        instance.ready = true;
        return instance;
      });
  }
  return instance;
};
